const API_URL = process.env.REACT_APP_API_URL || '/api';

/** How a count was taken. */
export type CountMethod = 'DRONE' | 'DIP' | 'PHYSICAL';
export type CountLineState = 'not-counted' | 'ready' | 'awaiting-sign-off' | 'investigating' | 'posted';

export const COUNT_METHOD_OPTIONS: { value: CountMethod; label: string; hint: string }[] = [
  { value: 'DRONE', label: 'Drone fly-over', hint: 'Stockpile volumes from a survey' },
  { value: 'DIP', label: 'Dip', hint: 'Tank and bladder readings' },
  { value: 'PHYSICAL', label: 'Physical count', hint: 'Counted or measured on site' },
];

export interface CountLine {
  id: string;
  bin: { id: string; code: string; locationId: string; locationName: string; locationType: string };
  material: { id: string; label: string; unitOfMeasure: string | null };
  expected: number;
  counted: number | null;
  variance: number | null;
  variancePct: number | null;
  outsideTolerance: boolean;
  state: CountLineState;
  note: string | null;
  posted: { at: string; by: string | null; acceptedOutsideTolerance: boolean; movementId: string | null } | null;
}

export interface CountSummary {
  id: string;
  site: { id: string; name: string; number: string | null };
  method: CountMethod;
  methodLabel: string;
  countedAt: string;
  tolerancePct: number;
  status: 'OPEN' | 'CLOSED';
  note: string | null;
  createdBy: string | null;
  createdAt: string;
  closedAt: string | null;
  lines: { total: number; counted: number; posted: number; investigating: number };
}

export interface CountDetail extends CountSummary {
  lineList: CountLine[];
  closeBlocker: string | null;
  can: { post: boolean; accept: boolean };
}

/** A refusal from the server, keeping its machine-readable code (and the open count's id on a 409). */
export class CountRequestError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
    public readonly countId?: string
  ) {
    super(message);
  }
}

async function send<T>(path: string, init: RequestInit = {}, fallback = 'The request failed'): Promise<T> {
  const response = await fetch(`${API_URL}/material-counts${path}`, {
    credentials: 'include',
    ...init,
    headers: init.body ? { 'Content-Type': 'application/json' } : undefined,
  });
  if (!response.ok) {
    let body: { error?: string; details?: string; code?: string; countId?: string } = {};
    try {
      body = await response.json();
    } catch {
      // keep the fallback
    }
    throw new CountRequestError(body.error || body.details || fallback, response.status, body.code, body.countId);
  }
  return (await response.json()) as T;
}

export const materialCountService = {
  list: async (filter: { siteId?: string; status?: 'OPEN' | 'CLOSED' } = {}): Promise<CountSummary[]> => {
    const query = new URLSearchParams();
    if (filter.siteId) query.set('siteId', filter.siteId);
    if (filter.status) query.set('status', filter.status);
    const body = await send<{ counts: CountSummary[] }>(`?${query}`, {}, 'Failed to load counts');
    return body.counts;
  },

  get: async (id: string): Promise<CountDetail> =>
    (await send<{ count: CountDetail }>(`/${encodeURIComponent(id)}`, {}, 'Failed to load the count')).count,

  /** Rejects with code COUNT_ALREADY_OPEN and the open count's id when the site already has one. */
  start: async (input: { siteId: string; method: CountMethod; countedAt: string; note: string }): Promise<CountDetail> =>
    (
      await send<{ count: CountDetail }>(
        '',
        { method: 'POST', body: JSON.stringify({ ...input, siteId: Number(input.siteId), note: input.note || null }) },
        'Failed to start the count'
      )
    ).count,

  updateLine: async (
    countId: string,
    lineId: string,
    patch: { countedQuantity?: number | null; investigating?: boolean; note?: string | null }
  ): Promise<CountDetail> =>
    (
      await send<{ count: CountDetail }>(
        `/${encodeURIComponent(countId)}/lines/${encodeURIComponent(lineId)}`,
        { method: 'PATCH', body: JSON.stringify(patch) },
        'Failed to save the line'
      )
    ).count,

  postLine: async (countId: string, lineId: string): Promise<CountDetail> =>
    (
      await send<{ count: CountDetail }>(
        `/${encodeURIComponent(countId)}/lines/${encodeURIComponent(lineId)}/post`,
        { method: 'POST' },
        'Failed to post the line'
      )
    ).count,

  postReady: async (countId: string): Promise<{ posted: number; count: CountDetail }> =>
    send(`/${encodeURIComponent(countId)}/post-ready`, { method: 'POST' }, 'Failed to post the lines'),

  close: async (countId: string): Promise<CountDetail> =>
    (await send<{ count: CountDetail }>(`/${encodeURIComponent(countId)}/close`, { method: 'POST' }, 'Failed to close the count')).count,
};
